import { AuthError, completeLogin, HomeboundUser, startLogin } from "@homebound/auth-components";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { amplifyConfig, getStage } from "src/context";
import { useViewport } from "src/contexts";
import { Button, Icon, REDIRECT_KEY } from "~components";
import { Css } from "~generated/css";
import { useTestIds } from "~utils";
import { AuthFormContainer } from "../components/AuthFormContainer";
import { CodeEntryField } from "../components/CodeEntryField";
import { LoadingOverlay } from "../components/LoadingOverlay";

type EnterCodeProps = {
  onLogin: (user: HomeboundUser) => Promise<void>;
  isInternal?: boolean;
};

// aka Verify Code
export function EnterCode(props: EnterCodeProps) {
  const tid = useTestIds(props, "enterCode");
  const { onLogin } = props;
  const [code, setCode] = useState("");
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [codeResent, setCodeResent] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { linkCode } = useParams<{ linkCode?: string }>();
  const { isMobile } = useViewport();

  // Get email from location state (passed from SignIn or SignUp)
  const email = location.state?.email;
  const isSignup = location.state?.isSignUp || false;

  // Handle magic link code from route params on mount
  // Technically, a user could be in the sign up flow, receive their magic link/code email, click the link and then be redirected here
  // where they will no longer see the sign up state (i.e. they will instead see the standard sign in UI). But that's the price we pay for reusing the same generic magic link.
  // Should be fine since a user will only be seeing that for a few seconds as it's authenticated.
  useEffect(() => {
    if (linkCode !== undefined) {
      void submitCode(linkCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submitCode(codeValue: string) {
    setDisabled(true);

    try {
      // Always use homeowner pool for magic link code validation
      const { data: user, error } = await completeLogin(amplifyConfig(getStage(), false), codeValue);

      if (user) {
        // Handle the complete authentication flow: set user, refetch BP data, then set authenticated
        await onLogin(user);
        setDisabled(false);

        // Redirect to original URL if provided
        // Check query params first (for manual code entry), then localStorage (for magic link)
        const redirectUrl = searchParams.get("redirect") || localStorage.getItem(REDIRECT_KEY);
        if (redirectUrl) {
          localStorage.removeItem(REDIRECT_KEY); // Clean up
          navigate(redirectUrl);
        }
      } else {
        setDisabled(false);
        setError(error || null);
      }
    } catch {
      setDisabled(false);
      setError({ code: "NotAuthorizedException", message: "" });
    }
  }

  async function handleCode() {
    setError(null);
    setCodeResent(false);
    await submitCode(code);
  }

  async function handleResendCode() {
    if (!email) {
      // If we don't have an email in state, navigate back to sign in
      navigate("/signIn");
      return;
    }
    setCodeResent(true);
    setError(null);
    setDisabled(true);
    const { error } = await startLogin(email);
    setDisabled(false);
    if (error) {
      setError(error);
    }
  }

  return (
    <AuthFormContainer
      title={isSignup ? "Sign Up" : "Login"}
      onBack={() => {
        if (isSignup) {
          navigate("/signUp");
        } else {
          navigate("/signIn");
        }
      }}
    >
      <div
        css={isMobile ? Css.df.fdc.aic.gap3.px4.jcc.if(!isSignup).mt6.$ : Css.df.fdc.jcc.aic.vh100.gap3.wPx(500).mxa.$}
      >
        {!isMobile && <img src="/logo.svg" width="153" height="26" alt="Homebound" />}
        <div css={Css.px5.py4.$}>
          <Icon name="emailCode" />
        </div>
        {isSignup ? (
          <>
            <div css={Css.t24.gray800.pt1.pb2.tac.$}>You're almost there!</div>
            <div css={Css.t16.gray.mb2.tac.$}>
              Enter the code sent to
              <br />
              {email}.
              <br />
              Expires in 10 minutes.
            </div>
          </>
        ) : (
          <>
            <div css={Css.t16.gray.mb2.tac.$}>
              Enter the code sent to your email.
              <br />
              <br />
              Expires in 10 minutes.
            </div>
          </>
        )}
        <CodeEntryField
          error={error ? friendlyErrorMessage(error) : null}
          codeResent={codeResent}
          value={code}
          onChange={setCode}
          onEnter={() => void handleCode()}
          disabled={disabled}
        />

        <Button {...tid.signInButton} variant="primary" disabled={!code || disabled} onClick={() => handleCode()}>
          {isSignup ? "Complete sign up" : "Sign in"}
        </Button>

        <div css={Css.t14.gray800.$} {...tid.resendCodeButton}>
          Didn't receive code?&nbsp;
          <Button variant="inline" onClick={() => void handleResendCode()} disabled={disabled}>
            Resend to email
          </Button>
        </div>
      </div>
      {disabled && <LoadingOverlay />}
    </AuthFormContainer>
  );
}

function friendlyErrorMessage(error: AuthError): string {
  switch (error.code) {
    case "NotAuthorizedException":
      return "The code is incorrect. Please try again or resend code.";
    case "ValidationError":
      return error.message || "Please enter a valid code";
    default:
      return "Uh oh! Something went wrong";
  }
}
